// Helper functions

// Status color mapping
export const getStatusColor = (status) => {
  switch (status) {
    case 'ปกติ': return 'bg-green-100 text-green-800';
    case 'สาย': return 'bg-red-100 text-red-800';
    case 'สาย(อนุมัติ)': return 'bg-yellow-100 text-yellow-800';
    case 'ไม่ลงเวลาเข้า': return 'bg-yellow-100 text-yellow-800';
    case 'ไม่ลงเวลาออก': return 'bg-orange-100 text-orange-800';
    case 'ไม่ลงเวลาเข้า(อนุมัติ)': return 'bg-blue-100 text-blue-800';
    case 'ไม่ลงเวลาออก(อนุมัติ)': return 'bg-orange-100 text-orange-800';
    case 'ออกก่อนเวลา': return 'bg-pink-100 text-pink-800';
    case 'ไม่ครบเวลา': return 'bg-yellow-100 text-yellow-800';
    case 'กำลังทำงาน': return 'bg-blue-100 text-blue-800';
    case 'ทำงานน้อย': return 'bg-orange-100 text-orange-800';
    case 'ไม่มาปฏิบัติงาน': return 'bg-gray-100 text-gray-800';
    case 'ลาป่วย': return 'bg-pink-100 text-pink-800';
    case 'ลากิจ': return 'bg-purple-100 text-purple-800';
    case 'ลาคลอด': return 'bg-cyan-100 text-cyan-800';
    case 'ไปราชการ': return 'bg-blue-100 text-blue-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

// Calculate distance between two coordinates
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  if (empty(lat1) || empty(lon1) || empty(lat2) || empty(lon2)) {
    return 0;
  }
  
  const earthRadius = 6371; // km
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) + 
           Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return earthRadius * c;
};

const deg2rad = (deg) => {
  return deg * (Math.PI/180);
};

const empty = (value) => {
  return value === null || value === undefined || value === '';
};

// Format date for Thai locale
export const formatDateThai = (date) => {
  return new Date(date).toLocaleDateString('th-TH', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

// Format time for display
export const formatTime = (time) => {
  if (!time) return '-';
  return time;
};

// Get current location with fallback
export const getCurrentLocation = () => {
  return new Promise((resolve, reject) => {
    // Check for secure context
    const isSecureContext = window.location.protocol === 'https:' || 
                           window.location.hostname === 'localhost' || 
                           window.location.hostname === '127.0.0.1';

    if (!isSecureContext) {
      console.warn('Non-secure context, using default location');
      resolve({
        latitude: 13.7563,
        longitude: 100.5018,
        accuracy: 1000,
        manual: true
      });
      return;
    }

    if (!navigator.geolocation) {
      resolve({
        latitude: 13.7563,
        longitude: 100.5018,
        accuracy: 1000,
        manual: true
      });
      return;
    }

    const options = {
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 300000
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        });
      },
      (error) => {
        console.error('Geolocation error:', error);
        
        // Try with fallback options
        const fallbackOptions = {
          enableHighAccuracy: false,
          timeout: 30000,
          maximumAge: 600000
        };
        
        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy
            });
          },
          (fallbackError) => {
            console.error('Fallback geolocation error:', fallbackError);
            resolve({
              latitude: 13.7563,
              longitude: 100.5018,
              accuracy: 1000,
              manual: true
            });
          },
          fallbackOptions
        );
      },
      options
    );
  });
};

// Sort employees by department and ID
export const sortEmployeesByDepartmentAndId = (employees) => {
  return employees.sort((a, b) => {
    // Sort by department first
    const deptA = (a.department || '').toLowerCase();
    const deptB = (b.department || '').toLowerCase();
    
    if (deptA !== deptB) {
      return deptA.localeCompare(deptB);
    }
    
    // Then by employee_id
    return a.employee_id.localeCompare(b.employee_id);
  });
};

// Merge employee data with attendance data
export const mergeEmployeeAttendance = (employees, attendanceData) => {
  console.log('Merging employees:', employees.length, 'attendance records:', attendanceData.length);
  
  const attendanceMap = {};
  
  // Create a map of attendance data by employee_id
  attendanceData.forEach(record => {
    attendanceMap[record.employee_id] = record;
  });
  
  // Merge employee data with attendance data
  const mergedData = employees.map(employee => {
    const attendance = attendanceMap[employee.employee_id];
    
    const result = {
      ...employee,
      work_date: attendance?.work_date || null,
      check_in: attendance?.check_in || null,
      check_out: attendance?.check_out || null,
      work_hours: attendance?.work_hours || null,
      status: attendance?.status || 'ไม่มาปฏิบัติงาน',
      attendance_id: attendance?.id || attendance?.attendance_id || null,
      attendance_notes: attendance?.notes || attendance?.attendance_notes || '',
      check_in_image: attendance?.check_in_image || null,
      check_out_image: attendance?.check_out_image || null
    };
    
    // Debug log for images
    if (attendance?.check_in_image) {
      console.log(`Employee ${employee.employee_id} has check_in_image:`, attendance.check_in_image.substring(0, 50) + '...');
    }
    if (attendance?.check_out_image) {
      console.log(`Employee ${employee.employee_id} has check_out_image:`, attendance.check_out_image.substring(0, 50) + '...');
    }
    
    return result;
  });
  
  console.log('Merged result:', mergedData.length, 'records');
  return mergedData;
};

// Camera helper functions
export const getCameraConstraints = () => {
  return {
    video: {
      width: { ideal: 640, max: 1280 },
      height: { ideal: 480, max: 720 },
      facingMode: 'user',
      frameRate: { ideal: 30, max: 30 }
    },
    audio: false
  };
};

export const getSimpleCameraConstraints = () => {
  return {
    video: {
      facingMode: 'user'
    },
    audio: false
  };
};

// Print helper function
export const printReport = (content, title) => {
  // สร้าง CSS styles สำหรับการพิมพ์ A4 แนวตั้ง
  const printStyles = `
    <style>
      @page {
        size: a4 portrait; /* กำหนดเป็น A4 แนวตั้ง */
        margin: 1.5cm;     /* ตั้งค่าระยะขอบกระดาษ */
      }

      body {
          font-family: 'Sarabun';
          /* [✅ แก้ไข] เปลี่ยน URL เป็น Server ใหม่ (krutof.work) */
          src: url('https://rnn.krutof.work/smartface/api/fonts/THSarabun.ttf') format('truetype');
          font-weight: 300;
          font-style: normal;
      }

      .header {
        text-align: center;
        margin-bottom: 20px;
        border-bottom: 1px solid #ccc;
        padding-bottom: 10px;
      }

      .logo {
        max-height: 70px; /* ปรับขนาดโลโก้ไม่ให้ใหญ่เกินไป */
        max-width: 100%;
        margin-bottom: 10px;
      }

      .org-name {
        font-size: 14pt;
        font-weight: bold;
        margin-bottom: 5px;
      }

      .date {
        font-size: 12pt;
        font-weight: bold;
      }

      table {
        width: 100%;
        border-collapse: collapse;
        margin-bottom: 15px;
        font-size: 9pt; /* ย่อขนาดฟอนต์ในตารางเล็กน้อยเพื่อให้พอดี */
      }

      table thead {
        display: table-header-group; /* ให้หัวตารางแสดงซ้ำในหน้าถัดไป */
        background-color: #f3f4f6;
      }

      table th, table td {
        border: 1px solid #ddd;
        padding: 6px;
        text-align: left;
        word-wrap: break-word; /* ตัดคำขึ้นบรรทัดใหม่ถ้าข้อความยาว */
      }
      
      table th {
        font-weight: bold;
      }

      /* สรุปผล (สำหรับ DailyReport) */
      .summary-stats {
        margin-bottom: 20px;
        padding: 10px;
        border: 1px solid #eee;
        background-color: #f9f9f9;
        page-break-inside: avoid; /* พยายามไม่ให้ส่วนนี้ถูกตัดขึ้นหน้าใหม่ */
      }
      
      .summary-stats h3 {
        font-size: 12pt;
        font-weight: bold;
        margin-bottom: 10px;
        margin-top: 0;
      }
      
      .stats-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr); /* แบ่งเป็น 3 คอลัมน์ */
        gap: 5px;
      }
      
      .stat-item {
        display: flex;
        justify-content: space-between;
        font-size: 9pt;
      }
      
      .stat-label {
        font-weight: bold;
        padding-right: 10px;
      }
      
      .stat-value {
        text-align: right;
      }

      /* ส่วนลายเซ็น */
      .signature {
        margin-top: 40px;
        text-align: right;
        font-size: 10pt;
        page-break-inside: avoid;
      }

      .signature-line {
        width: 250px;
        border-bottom: 1px dotted #000;
        margin: 40px 0 5px auto;
      }
    </style>
  `;

  // เปิดหน้าต่างใหม่สำหรับพิมพ์
  const printWindow = window.open('', '_blank', 'width=800,height=600');
  
  if (!printWindow) {
    alert('ไม่สามารถเปิดหน้าต่างสำหรับพิมพ์ได้ กรุณาตรวจสอบการตั้งค่า Pop-up Blocker');
    return;
  }

  // เขียน HTML content และ CSS styles ลงในหน้าต่างใหม่
  printWindow.document.write(`
    <html>
      <head>
        <title>${title}</title>
        <meta charset="utf-8">
        ${printStyles} 
      </head>
      <body>
        ${content}
      </body>
    </html>
  `);

  printWindow.document.close();
  
  // ใช้ setTimeout เพื่อให้แน่ใจว่าโหลด CSS เสร็จก่อน
  setTimeout(() => {
    printWindow.print(); // สั่งพิมพ์
    printWindow.close(); // ปิดหน้าต่างหลังพิมพ์ (อาจจะไม่ทำงานในบางเบราว์เซอร์)
  }, 500); // รอ 0.5 วินาที
};


export const formatDays = (days) => {
  const num = parseFloat(days);
  if (isNaN(num)) {
    return '-'; // หรือ 0 ตามความเหมาะสม
  }
  
  // ตรวจสอบว่าเป็นเลขจำนวนเต็มหรือไม่
  if (num % 1 === 0) {
    return num.toFixed(0); // แสดงผล "3"
  }
  
  // ถ้าเป็นทศนิยม ให้แสดง 1 ตำแหน่ง
  return num.toFixed(1); // แสดงผล "3.5"
};

// [✅ เพิ่ม] Helper สำหรับแปลง VAPID Key เป็น Uint8Array
export const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};